import { ORDER_TYPE } from 'constants/trading';
import { SmartOrderCode } from 'global/Securities/types/transaction';
import { withMaintenanceGuard } from 'global/Securities/utils/maintenance';
import tradingModalStore from 'global/TradingModal/store';
import useTradingAccount from 'hooks/useTradingAccount';
import {
  clevertapOrderDetailAmend,
  clevertapOrderDetailAmendViewed,
  clevertapOrderDetailCancel,
  clevertapOrderDetailCancelConfirmed,
} from 'lib/analytics/securities';
import {
  VIRTUAL_TRADING_TYPES,
  virtualTradingAction,
  virtualTradingNavigate,
} from 'lib/analytics/virtualTrading';
import tradingDataStore from 'lib/stores/tradingData';
import tradingOperationStore from 'lib/stores/tradingOperation';
import { MouseEventHandler } from 'react';
import { generateOrderExpiryLabel } from 'utils/securities/analytics';
import shallow from 'zustand/shallow';
import useTradingStore from 'features/transaction/useTradingStore';
import { AutoSellType } from 'global/Securities/types/autoOrder';
import { MODAL_TYPE } from 'global/TradingModal/RealTradingModal/types';
import { ActionTarget } from 'global/Securities/types/store/order';
import { iRecord } from './types';

interface useOrderDetailActionParams {
  data: iRecord;
  onCancel(): void;
  onSubmit?: () => void;
}

const orderTypesToAmend = {
  real: {
    [ORDER_TYPE.BUY]: ORDER_TYPE.AMEND_BUY,
    [ORDER_TYPE.SELL]: ORDER_TYPE.AMEND_SELL,
  },
  virtual: {
    [ORDER_TYPE.BUY]: ORDER_TYPE.VIRTUAL_AMEND_BUY,
    [ORDER_TYPE.SELL]: ORDER_TYPE.VIRTUAL_AMEND_SELL,
  },
};

const useOrderDetailAction = ({
  data,
  onCancel,
  onSubmit,
}: useOrderDetailActionParams) => {
  const {
    symbol,
    cancel_target,
    day_trade_is_day_trade: isDayTrade,
  } = data || {};

  const { isRealTrading } = useTradingAccount();
  const showTradingModal = tradingModalStore((s) => s.showModal);
  const setModal = useTradingStore((s) => s.setModal);

  const {
    postTradeCancel,
    postDayTradeCancel,
    deleteTrailingStop,
    deleteStopOrder,
    deleteBracketOrder,
  } = tradingOperationStore(
    (s) => ({
      postTradeCancel: s.postTradeCancel,
      postDayTradeCancel: s.postDayTradeCancel,
      deleteTrailingStop: s.deleteTrailingStop,
      deleteStopOrder: s.deleteStopOrder,
      deleteBracketOrder: s.deleteBracketOrder,
    }),
    shallow,
  );

  const { getOrderHistory, getPortfolio } = tradingDataStore(
    (s) => ({
      getOrderHistory: s.getOrderHistory,
      getPortfolio: s.getPortfolio,
    }),
    shallow,
  );

  const handleCancelOrderRequest = async () => {
    if (isRealTrading) {
      clevertapOrderDetailCancelConfirmed({
        symbol,
        orderType: data?.action,
        expiry: generateOrderExpiryLabel(data?.gtc),
        leverage: isDayTrade ? 'Day Trade' : 'Regular',
      });
    }

    if (cancel_target === ActionTarget.TRAILING_STOP) {
      await deleteTrailingStop(data.orderid);
      getOrderHistory(isRealTrading);
      return;
    }

    if (cancel_target === ActionTarget.BRACKET_ORDER) {
      await deleteBracketOrder(data.orderid);
      getOrderHistory(isRealTrading);
      return;
    }

    if (
      cancel_target === ActionTarget.STOP_ORDER ||
      cancel_target === ActionTarget.LEGACY_SMARTORDER
    ) {
      await deleteStopOrder(data.orderid);
      getOrderHistory(isRealTrading);
      return;
    }

    if (cancel_target === ActionTarget.DAY_TRADE) {
      await postDayTradeCancel(data.orderid);
      getOrderHistory(isRealTrading);
      return;
    }

    await postTradeCancel({
      orderkey: `W-WITHDRAW-${Date.now()}`,
      orderid: data.orderid,
      gtc: data.gtc,
      symbol,
      isRealTrading,
    });
    await getPortfolio(isRealTrading);
    getOrderHistory(isRealTrading);
  };

  const handleCancelOrderClick: MouseEventHandler<HTMLButtonElement> = (
    event,
  ) => {
    event.stopPropagation();

    withMaintenanceGuard(async () => {
      await handleCancelOrderRequest();
      if (isRealTrading) {
        clevertapOrderDetailCancel(
          VIRTUAL_TRADING_TYPES.realTrading,
          symbol,
          data.action,
        );
      } else {
        virtualTradingAction.onOrderDetailCancelOrderSelected(symbol);
      }

      onSubmit?.();
    });
  };

  const handleAmendOrderClick: MouseEventHandler<HTMLButtonElement> = (
    event,
  ) => {
    event.stopPropagation();

    withMaintenanceGuard(() => {
      const useFederatedForm = true;
      const isAutoOrder = [
        AutoSellType.StopLoss,
        AutoSellType.TakeProfit,
      ].includes(data?.label as AutoSellType);

      if (isAutoOrder && !useFederatedForm) {
        setModal(MODAL_TYPE.AUTO_ORDER, {
          symbol,
          autoOrderId: data?.orderid,
        });

        onCancel();
        return;
      }

      const activeOrderTypes = isRealTrading
        ? orderTypesToAmend.real
        : orderTypesToAmend.virtual;

      const modalState = {
        symbol,
        group: data?.group,
        orderType: activeOrderTypes[data?.action],
        orderid: data.orderid,
        orderedLot: isRealTrading ? data?.order_total : data?.order_open,
        orderedPrice: Number(isRealTrading ? data?.price_order : data?.price),
        isAutoBuy:
          data.label === SmartOrderCode.AutoBuy && data.status === 'READY',
        modalData: data,
        isDayTrade,
        trailingStop: data?.trailing_stop,
        isGtc: data?.gtc,
      };

      showTradingModal(modalState);

      if (isRealTrading) {
        clevertapOrderDetailAmend({
          symbol,
        });
        clevertapOrderDetailAmendViewed({
          symbol,
          orderType: data.action,
          type: VIRTUAL_TRADING_TYPES.realTrading,
        });
      } else {
        virtualTradingNavigate.onAmendOrderViewed({
          Symbol: symbol,
          OrderType: data.action,
          Type: isRealTrading
            ? VIRTUAL_TRADING_TYPES.realTrading
            : VIRTUAL_TRADING_TYPES.virtualTrading,
        });

        virtualTradingAction.onOrderDetailAmendSelected({
          Symbol: symbol,
          Type: isRealTrading
            ? VIRTUAL_TRADING_TYPES.realTrading
            : VIRTUAL_TRADING_TYPES.virtualTrading,
        });
      }

      onCancel();
    });
  };

  return { handleAmendOrderClick, handleCancelOrderClick };
};

export default useOrderDetailAction;
